import React, { useEffect, useState } from 'react';
import { Button, DatePicker, Empty, Form, Input, Pagination, Select, Space, Spin } from 'antd';
import type { Dayjs } from 'dayjs';
import { getOrderInfoApi } from '@/api/member/member';
import styles from '../../index.module.scss';

const { RangePicker } = DatePicker;

export const orderStatusOptions = [
  { value: '', label: '全部' },
  { value: 'TRADE_PAID', label: '已支付' },
  { value: 'WAIT_SELLER_SEND_GOODS', label: '待发货' },
  { value: 'SELLER_CONSIGNED_PART', label: '部分发货' },
  { value: 'WAIT_BUYER_CONFIRM_GOODS', label: '待收货' },
  { value: 'TRADE_FINISHED', label: '交易成功' },
  { value: 'TRADE_CLOSED_ALL_REFUND', label: '退款成功' },
  { value: 'PAID_FORBID_CONSIGN', label: '已付款禁止发货' },
];

const OrderInfoTab: React.FC<{ oneId: string; shopId?: number | null; active: boolean }> = ({
  oneId,
  shopId,
  active,
}) => {
  const [form] = Form.useForm();
  const [stats, setStats] = useState<{ allBuyAmt?: number; allBuyCount?: number; unitPrice?: number }>({});
  const [orderList, setOrderList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    form.resetFields();
    setStats({});
    setOrderList([]);
    setTotal(0);
    setPageNum(1);
    setPageSize(10);
    setInitialized(false);
  }, [oneId, shopId, form]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!active || !oneId || initialized) return;
    handleSearch(1);
    setInitialized(true);
  }, [active, initialized, oneId]);

  const fetchOrderInfo = async (override?: Partial<{ pageNum: number; pageSize: number }>) => {
    const values = form.getFieldsValue();
    const orderRange: Dayjs[] | undefined = values.orderTime;
    const finishRange: Dayjs[] | undefined = values.finishTime;
    const params = {
      oneId,
      shopId: shopId || undefined,
      orderId: values.orderId || undefined,
      skuName: values.skuName || undefined,
      skuId: values.skuId || undefined,
      orderStartTime: orderRange?.[0] ? orderRange[0].startOf('day').format('YYYY-MM-DD HH:mm:ss') : undefined,
      orderEndTime: orderRange?.[1] ? orderRange[1].endOf('day').format('YYYY-MM-DD HH:mm:ss') : undefined,
      orderFinishStartTime: finishRange?.[0] ? finishRange[0].startOf('day').format('YYYY-MM-DD HH:mm:ss') : undefined,
      orderFinishEndTime: finishRange?.[1] ? finishRange[1].endOf('day').format('YYYY-MM-DD HH:mm:ss') : undefined,
      orderStatus: values.orderStatus || undefined,
      pageNum: override?.pageNum || pageNum,
      pageSize: override?.pageSize || pageSize,
    } as Record<string, any>;
    Object.keys(params).forEach(key => {
      if (params[key] === undefined || params[key] === '') {
        delete params[key];
      }
    });
    setLoading(true);
    try {
      const res = await getOrderInfoApi(params);
      if (res?.code === 200 || res?.code === 0) {
        const data = res.data || {};
        setStats({
          allBuyAmt: Number(data.allBuyAmt) || 0,
          allBuyCount: data.allBuyCount || 0,
          unitPrice: Number(data.unitPrice) || 0,
        });
        setOrderList(data.orderDetailList || []);
        setTotal(data.total || 0);
        if (override?.pageNum) setPageNum(override.pageNum);
        if (override?.pageSize) setPageSize(override.pageSize);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (resetPage?: number) => {
    fetchOrderInfo({ pageNum: resetPage ?? 1 });
  };

  const handleReset = () => {
    form.resetFields();
    handleSearch(1);
  };

  const handlePageChange = (page: number, size?: number) => {
    fetchOrderInfo({ pageNum: page, pageSize: size });
  };

  const renderStatusLabel = (status: string | undefined) => {
    if (!status) return '-';
    const found = orderStatusOptions.find(opt => opt.value === status);
    return found?.label || status;
  };

  const renderOrderItem = (order: any) => {
    return (
      <div
        key={order.orderId}
        className={styles['order-card']}>
        <div className={styles['order-card__header']}>
          <span>订单编号：{order.orderId}</span>
          <span>下单时间：{order.orderStartTime || '-'}</span>
          <span>付款时间：{order.orderPaymentTime || '-'}</span>
          <span>订单完成时间：{order.orderEndTime || '-'}</span>
        </div>
        {(order.orderSkuList || []).map((sku: any) => (
          <div
            key={sku.skuId}
            className={styles['order-card__sku']}>
            <img
              src={sku.skuImg}
              alt=""
            />
            <div className={styles['order-card__sku-info']}>
              <div className={styles['order-card__sku-title']}>{sku.skuName}</div>
              <div className={styles['order-card__sku-meta']}>
                <span>商品编号：{sku.skuId || '-'}</span>
                <span>数量：X{sku.skuNum || 0}</span>
              </div>
            </div>
            <div className={styles['order-card__sku-price']}>￥{sku.skuPrice || '0.00'}</div>
          </div>
        ))}
        <div className={styles['order-card__footer']}>
          <div>
            <div>渠道：{order.platformName || '-'}</div>
            <div>店铺：{order.shopName || '-'}</div>
          </div>
          <div>
            <div>订单金额：￥{order.orderAmt || order.payAmt || '0.00'}</div>
            <div>订单状态：{renderStatusLabel(order.orderStatus)}</div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles['order-section']}>
      <Form
        form={form}
        layout="inline"
        className={styles['order-filter']}>
        <Form.Item
          label="订单编号"
          name="orderId">
          <Input
            placeholder="请输入"
            allowClear
          />
        </Form.Item>
        <Form.Item
          label="商品名称"
          name="skuName">
          <Input
            placeholder="请输入"
            allowClear
          />
        </Form.Item>
        <Form.Item
          label="SKU编号"
          name="skuId">
          <Input
            placeholder="请输入"
            allowClear
          />
        </Form.Item>
        <Form.Item
          label="下单时间"
          name="orderTime">
          <RangePicker />
        </Form.Item>
        <Form.Item
          label="完成时间"
          name="finishTime">
          <RangePicker />
        </Form.Item>
        <Form.Item
          label="订单状态"
          name="orderStatus">
          <Select
            style={{ width: 160 }}
            options={orderStatusOptions}
            defaultValue=""
          />
        </Form.Item>
        <Form.Item>
          <Space>
            <Button
              type="primary"
              onClick={() => handleSearch(1)}>
              查询
            </Button>
            <Button onClick={handleReset}>重置</Button>
          </Space>
        </Form.Item>
      </Form>

      <div className={styles['order-stat']}>
        <div>
          <div>累计购买金额</div>
          <div>￥{stats.allBuyAmt?.toFixed(2) || '0.00'}</div>
        </div>
        <div>
          <div>累计下单次数</div>
          <div>{stats.allBuyCount || 0}</div>
        </div>
        <div>
          <div>笔单价</div>
          <div>￥{stats.unitPrice?.toFixed(2) || '0.00'}</div>
        </div>
      </div>

      <Spin spinning={loading}>
        {orderList.length ? orderList.map(renderOrderItem) : <Empty description="暂无订单" />}
      </Spin>

      <div className={styles['order-pagination']}>
        <Pagination
          current={pageNum}
          pageSize={pageSize}
          total={total}
          showSizeChanger
          showTotal={t => `共 ${t} 条`}
          onChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default OrderInfoTab;
